/**
 * HTTP API DTOs (docs/01-build-plan.md §5.5). Every request/response body is declared here so the editor,
 * the docs and the server agree. Dates are ISO strings on the wire.
 */
import { z } from 'zod';
import { CalendarOptions } from '@freealbum/document';
import { AlbumDocument, ExportOptions, FormatIdSchema, PageSize, PhotoInfo, PrintTarget, PAGE_COUNT_LIMITS, TAG_LIMITS, TagList } from '@freealbum/document';
import { ALLOWED_PHOTO_MIMES, FONT_FORMATS } from './limits';
import { ApiErrorBody } from './errors';

export { PhotoInfo } from '@freealbum/document';
export type { PhotoMap } from '@freealbum/document';

const Iso = z.string();
const IsoNullable = z.string().nullable();

export const AlbumStatus = z.enum(['active', 'purged', 'deleted']);
export type AlbumStatus = z.infer<typeof AlbumStatus>;
export const PhotoStatus = z.enum(['uploading', 'processing', 'ready', 'failed', 'purged']);
export type PhotoStatus = z.infer<typeof PhotoStatus>;
export const ExportStatus = z.enum(['queued', 'rendering', 'done', 'failed', 'cancelled']);
export type ExportStatus = z.infer<typeof ExportStatus>;
export const ShareKind = z.enum(['contribute', 'flipbook']);
export type ShareKind = z.infer<typeof ShareKind>;
export const UserRole = z.enum(['user', 'admin']);
export type UserRole = z.infer<typeof UserRole>;
export const AlbumLang = z.enum(['he', 'en']);

// ---------- me ----------
export const UserDto = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  isAnonymous: z.boolean(),
  role: UserRole,
  retentionUnlimited: z.boolean(),
  storageUsedBytes: z.number(),
  storageQuotaBytes: z.number(),
});
export type UserDto = z.infer<typeof UserDto>;

/**
 * `user` is null for a browser that has never started anything. Visiting a page must not mint a
 * user row — the session is created when work begins (creating an album, or signing in), so a
 * crawler or a bounce costs nothing and the landing page has nothing to wait for.
 */
export const MeResponse = z.object({
  user: UserDto.nullable(),
  features: z.object({
    google: z.boolean(),
    /** Email/password sign-in, including legacy local seeded-account access. */
    emailPassword: z.boolean(),
    /** Whether a visitor can create a new email/password account. */
    emailRegistration: z.boolean(),
    /** Legacy local credential switch, retained so tooling can identify developer mode. */
    devLogin: z.boolean(),
  }),
});
export type MeResponse = z.infer<typeof MeResponse>;

// ---------- albums ----------
export const AlbumSummary = z.object({
  id: z.string(),
  title: z.string(),
  format: FormatIdSchema,
  page: PageSize,
  innerPages: z.number().int(),
  coverThumbUrl: z.string().nullable(),
  updatedAt: Iso,
  status: AlbumStatus,
  purgeAt: IsoNullable,
  lastExportedAt: IsoNullable,
  photoCount: z.number().int(),
});
export type AlbumSummary = z.infer<typeof AlbumSummary>;

export const AlbumDto = AlbumSummary.extend({
  ownerId: z.string(),
  lang: AlbumLang,
  themeId: z.string(),
  retentionExtensions: z.number().int(),
  createdAt: Iso,
  lastActivityAt: Iso,
});
export type AlbumDto = z.infer<typeof AlbumDto>;

export const AlbumsResponse = z.object({ albums: z.array(AlbumSummary) });
export type AlbumsResponse = z.infer<typeof AlbumsResponse>;

export const CreateAlbumRequest = z.object({
  calendar: CalendarOptions.optional(),
  format: FormatIdSchema,
  /** required when format === 'custom' */
  page: PageSize.optional(),
  innerPages: z.number().int().min(PAGE_COUNT_LIMITS.min).max(PAGE_COUNT_LIMITS.max).multipleOf(2).default(20),
  title: z.string().max(200).default(''),
  lang: AlbumLang.default('he'),
  themeId: z.string().max(64).default('family'),
  /** where it will be printed, if the wizard was told */
  print: PrintTarget.partial().optional(),
});
export type CreateAlbumRequest = z.infer<typeof CreateAlbumRequest>;

export const AlbumWithDocResponse = z.object({ album: AlbumDto, doc: AlbumDocument, version: z.number().int() });
export type AlbumWithDocResponse = z.infer<typeof AlbumWithDocResponse>;

export const GetAlbumResponse = AlbumWithDocResponse.extend({ photos: z.array(PhotoInfo) });
export type GetAlbumResponse = z.infer<typeof GetAlbumResponse>;

export const PatchAlbumRequest = z.object({
  /** validated server-side with parseAlbumDocument */
  doc: z.unknown().optional(),
  title: z.string().max(200).optional(),
  version: z.number().int().nonnegative(),
});
export type PatchAlbumRequest = z.infer<typeof PatchAlbumRequest>;

export const PatchAlbumResponse = z.object({ version: z.number().int() });
export type PatchAlbumResponse = z.infer<typeof PatchAlbumResponse>;

export const VersionConflictResponse = ApiErrorBody.extend({ version: z.number().int(), doc: AlbumDocument });
export type VersionConflictResponse = z.infer<typeof VersionConflictResponse>;

export const AlbumResponse = z.object({ album: AlbumDto });
export type AlbumResponse = z.infer<typeof AlbumResponse>;

export const ExtendRetentionResponse = z.object({ purgeAt: IsoNullable });
export type ExtendRetentionResponse = z.infer<typeof ExtendRetentionResponse>;

// ---------- photos ----------
export const PhotosResponse = z.object({ photos: z.array(PhotoInfo) });
export type PhotosResponse = z.infer<typeof PhotosResponse>;

export const PresignFile = z.object({
  name: z.string().min(1).max(255),
  mime: z.enum(ALLOWED_PHOTO_MIMES),
  bytes: z.number().int().positive(),
});
export type PresignFile = z.infer<typeof PresignFile>;

export const PresignRequest = z.object({ files: z.array(PresignFile).min(1).max(100), resumePhotoId: z.string().optional() });
export type PresignRequest = z.infer<typeof PresignRequest>;

export const PresignUpload = z.object({
  photoId: z.string(),
  url: z.string(),
  key: z.string(),
  headers: z.record(z.string(), z.string()),
});
export type PresignUpload = z.infer<typeof PresignUpload>;
export const PresignResponse = z.object({ uploads: z.array(PresignUpload) });
export type PresignResponse = z.infer<typeof PresignResponse>;

export const PhotoResponse = z.object({ photo: PhotoInfo });
export type PhotoResponse = z.infer<typeof PhotoResponse>;

/**
 * `PATCH /api/albums/:id/photos/tags`. One call for a single photo or a thousand: `add` / `remove`
 * adjust each photo's list, `set` replaces it. Names are normalised server-side (`normalizeTags`).
 */
export const PhotoTagsRequest = z
  .object({
    photoIds: z.array(z.string().min(1).max(64)).min(1).max(TAG_LIMITS.maxPerRequest),
    add: TagList.optional(),
    remove: TagList.optional(),
    set: TagList.optional(),
  })
  .refine((v) => v.add !== undefined || v.remove !== undefined || v.set !== undefined, { message: 'add, remove or set is required' });
export type PhotoTagsRequest = z.infer<typeof PhotoTagsRequest>;
/** Only what changed — no presigned URLs, so the editor keeps its thumbnails and tile identity. */
export const PhotoTagsResponse = z.object({ photos: z.array(z.object({ id: z.string(), tags: z.array(z.string()) })) });
export type PhotoTagsResponse = z.infer<typeof PhotoTagsResponse>;

export const GoogleSourceSchema = z.enum(['google-photos', 'google-drive']);
export const ImportItem = z.object({
  id: z.string().min(1).max(512),
  name: z.string().min(1).max(255),
  mime: z.string().min(1).max(100),
  baseUrl: z.string().url().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});
export type ImportItem = z.infer<typeof ImportItem>;
export const ImportRequest = z.object({ source: GoogleSourceSchema, items: z.array(ImportItem).min(1).max(200) });
export type ImportRequest = z.infer<typeof ImportRequest>;

// ---------- google ----------
export const GoogleConfigResponse = z.object({
  enabled: z.boolean(),
  clientId: z.string().nullable(),
  apiKey: z.string().nullable(),
  appId: z.string().nullable(),
  scopes: z.array(z.string()),
});
export type GoogleConfigResponse = z.infer<typeof GoogleConfigResponse>;

export const GooglePickerSessionResponse = z.object({
  sessionId: z.string(),
  pickerUri: z.string(),
  pollIntervalMs: z.number().int(),
  expireTime: z.string().nullable(),
});
export type GooglePickerSessionResponse = z.infer<typeof GooglePickerSessionResponse>;

export const GooglePickedItem = z.object({
  id: z.string(),
  name: z.string(),
  mime: z.string(),
  baseUrl: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  type: z.string(),
});
export type GooglePickedItem = z.infer<typeof GooglePickedItem>;
export const GooglePickerSessionStatus = z.object({ mediaItemsSet: z.boolean(), items: z.array(GooglePickedItem) });
export type GooglePickerSessionStatus = z.infer<typeof GooglePickerSessionStatus>;

// ---------- exports ----------
export const CreateExportRequest = z.object({ options: ExportOptions, version: z.number().int().nonnegative().optional() });
export type CreateExportRequest = z.infer<typeof CreateExportRequest>;

export const ExportJobDto = z.object({
  id: z.string(),
  albumId: z.string(),
  status: ExportStatus,
  progress: z.number().min(0).max(1),
  stage: z.string().nullable(),
  error: z.string().nullable(),
  options: ExportOptions,
  files: z.object({ album: z.string().optional(), cover: z.string().optional() }),
  flipbookReady: z.boolean(),
  createdAt: Iso,
  startedAt: IsoNullable,
  finishedAt: IsoNullable,
});
export type ExportJobDto = z.infer<typeof ExportJobDto>;

export const ExportJobResponse = z.object({ job: ExportJobDto });
export type ExportJobResponse = z.infer<typeof ExportJobResponse>;
export const ExportJobsResponse = z.object({ jobs: z.array(ExportJobDto) });
export type ExportJobsResponse = z.infer<typeof ExportJobsResponse>;

// ---------- share ----------
export const ShareCreateRequest = z.object({ kind: ShareKind });
export type ShareCreateRequest = z.infer<typeof ShareCreateRequest>;

export const ShareLinkDto = z.object({
  id: z.string(),
  kind: ShareKind,
  token: z.string(),
  url: z.string(),
  expiresAt: IsoNullable,
  createdAt: Iso,
});
export type ShareLinkDto = z.infer<typeof ShareLinkDto>;
export const ShareLinkResponse = z.object({ link: ShareLinkDto });
export type ShareLinkResponse = z.infer<typeof ShareLinkResponse>;
export const ShareLinksResponse = z.object({ links: z.array(ShareLinkDto) });
export type ShareLinksResponse = z.infer<typeof ShareLinksResponse>;

// ---------- contribute (no auth) ----------
export const ContributeInfoResponse = z.object({ album: z.object({ title: z.string(), format: FormatIdSchema }) });
export type ContributeInfoResponse = z.infer<typeof ContributeInfoResponse>;

export const ContributePresignRequest = z.object({
  contributorName: z.string().max(80).default(''),
  files: z.array(PresignFile).min(1).max(100),
});
export type ContributePresignRequest = z.infer<typeof ContributePresignRequest>;

// ---------- viewer ----------
export const ViewPage = z.object({
  url: z.string(), w: z.number().int(), h: z.number().int(),
  kind: z.enum(['cover-front', 'cover-back', 'page', 'spread']).optional(),
  /** Physical placement; a spread image can contain just one printed half. */
  side: z.enum(['left', 'right']).optional(),
});
export type ViewPage = z.infer<typeof ViewPage>;
export const ViewerBook = z.object({
  widthMm: z.number().positive(),
  heightMm: z.number().positive(),
  spineMm: z.number().min(0),
  pageCount: z.number().int().nonnegative(),
  binding: z.enum(['hardcover', 'softcover']),
  direction: z.enum(['rtl', 'ltr']),
  coverColor: z.string(),
  /** Flat colour for the spine where only a colour can be painted. Older feeds fall back to the cover colour. */
  spineColor: z.string().optional(),
  hasCover: z.boolean(),
});
export type ViewerBook = z.infer<typeof ViewerBook>;
export const ViewResponse = z.object({
  title: z.string(),
  pages: z.array(ViewPage),
  spreadMode: z.boolean(),
  /** content language of the album — a Hebrew album is paged right-to-left */
  lang: AlbumLang,
  /** Optional for older viewer feeds. Always derived from the exported document snapshot. */
  book: ViewerBook.optional(),
  /** presigned album.pdf of the export behind this link, or null when it has expired/was purged */
  downloadUrl: z.string().nullable(),
});
export type ViewResponse = z.infer<typeof ViewResponse>;

// ---------- fonts ----------
export const FontDto = z.object({
  id: z.string(),
  family: z.string(),
  weight: z.number().int(),
  style: z.enum(['normal', 'italic']),
  format: z.enum(FONT_FORMATS),
  url: z.string(),
  supportsHebrew: z.boolean(),
  supportsNikud: z.boolean(),
  bytes: z.number().int(),
  createdAt: Iso,
});
export type FontDto = z.infer<typeof FontDto>;
export const FontsResponse = z.object({ fonts: z.array(FontDto) });
export type FontsResponse = z.infer<typeof FontsResponse>;
export const FontResponse = z.object({ font: FontDto });
export type FontResponse = z.infer<typeof FontResponse>;

// ---------- admin ----------
export const AdminStatsResponse = z.object({
  users: z.number().int(),
  anonymousUsers: z.number().int(),
  albums: z.number().int(),
  photos: z.number().int(),
  storageBytes: z.number(),
  jobs: z.record(ExportStatus, z.number().int()),
  albumsPurgingWithin7Days: z.number().int(),
});
export type AdminStatsResponse = z.infer<typeof AdminStatsResponse>;

/** Bytes and object counts under one top-level storage prefix (docs/02-prd.md · ADM-4). */
export const StorageClass = z.enum(['originals', 'derivatives', 'exports', 'flipbook', 'fonts']);
export type StorageClass = z.infer<typeof StorageClass>;
export const AdminStorageClassDto = z.object({
  name: StorageClass,
  bytes: z.number(),
  objects: z.number().int(),
});
export type AdminStorageClassDto = z.infer<typeof AdminStorageClassDto>;
export const AdminStorageResponse = z.object({
  classes: z.array(AdminStorageClassDto),
  totalBytes: z.number(),
  totalObjects: z.number().int(),
  /** When the bucket was walked. The answer is cached briefly, so it can lag the panel. */
  measuredAt: Iso,
});
export type AdminStorageResponse = z.infer<typeof AdminStorageResponse>;

/** One day of the admin trend series. `date` is a UTC calendar day, `YYYY-MM-DD`. */
export const AdminTrendPoint = z.object({
  date: z.string(),
  users: z.number().int(),
  albums: z.number().int(),
  photos: z.number().int(),
  exports: z.number().int(),
});
export type AdminTrendPoint = z.infer<typeof AdminTrendPoint>;
export const AdminTrendsResponse = z.object({
  days: z.number().int(),
  series: z.array(AdminTrendPoint),
  /** Totals for the trailing window and the one before it, so the panel can state a direction. */
  recent: z.object({ users: z.number().int(), albums: z.number().int(), photos: z.number().int(), exports: z.number().int() }),
  previous: z.object({ users: z.number().int(), albums: z.number().int(), photos: z.number().int(), exports: z.number().int() }),
  /** Share of finished exports in the window that succeeded; null when none finished. */
  exportSuccessRate: z.number().nullable(),
});
export type AdminTrendsResponse = z.infer<typeof AdminTrendsResponse>;

export const AdminAuditDto = z.object({
  id: z.string(),
  action: z.string(),
  targetType: z.string(),
  targetId: z.string().nullable(),
  actorId: z.string().nullable(),
  actorEmail: z.string().nullable(),
  meta: z.record(z.string(), z.unknown()).nullable(),
  createdAt: Iso,
});
export type AdminAuditDto = z.infer<typeof AdminAuditDto>;
export const AdminAuditResponse = z.object({
  entries: z.array(AdminAuditDto),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  /** Every action present in the log, for the filter. */
  actions: z.array(z.string()),
});
export type AdminAuditResponse = z.infer<typeof AdminAuditResponse>;

export const AdminUserDto = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  isAnonymous: z.boolean(),
  role: UserRole,
  retentionUnlimited: z.boolean(),
  storageUsedBytes: z.number(),
  albumCount: z.number().int(),
  createdAt: Iso,
});
export type AdminUserDto = z.infer<typeof AdminUserDto>;
export const AdminUsersResponse = z.object({
  users: z.array(AdminUserDto),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type AdminUsersResponse = z.infer<typeof AdminUsersResponse>;

export const AdminUserPatchRequest = z.object({
  retentionUnlimited: z.boolean().optional(),
  role: UserRole.optional(),
});
export type AdminUserPatchRequest = z.infer<typeof AdminUserPatchRequest>;
export const AdminUserResponse = z.object({ user: AdminUserDto });
export type AdminUserResponse = z.infer<typeof AdminUserResponse>;

export const AdminJobDto = ExportJobDto.extend({ ownerId: z.string(), ownerEmail: z.string().nullable(), albumTitle: z.string() });
export type AdminJobDto = z.infer<typeof AdminJobDto>;
export const AdminJobsResponse = z.object({ jobs: z.array(AdminJobDto) });
export type AdminJobsResponse = z.infer<typeof AdminJobsResponse>;

export const AdminAlbumDto = AlbumSummary.extend({ ownerId: z.string(), ownerEmail: z.string().nullable(), retentionExtensions: z.number().int() });
export type AdminAlbumDto = z.infer<typeof AdminAlbumDto>;
export const AdminAlbumsResponse = z.object({ albums: z.array(AdminAlbumDto) });
export type AdminAlbumsResponse = z.infer<typeof AdminAlbumsResponse>;

export const OkResponse = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof OkResponse>;
